import { EntityType } from '@frontend/common';
import { ErrorHandler } from '@frontend/error-handler';
import { ProductAttributeClient, ProductClient } from '@frontend/product/api';
import { IProduct } from '@frontend/product/types';
import { useAccountRepository } from '@frontend/repository';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { triggerRefetch } from '../../../../redux/detail-modal-slice';
import { useAppDispatch } from '../../../../redux/store';
import { LinkProductsModalProps } from './link-products-modal.component';

interface ViewProps {
    currentAccount: string;
    formValues: { product_ids: string[] };
    selectedProducts: string[];
    productObjects: IProduct[];
    productAttributeValues: Record<string, string | null>;
    fetchAvailableProducts: (queryParams?: any) => Promise<any>;
    setSelectedProducts: (products: string[]) => void;
    onAttributeValueChange: (productId: string, valueId: string | null) => void;
    onSubmit: () => Promise<{ success: boolean }>;
    isSubmitDisabled: () => boolean;
}

export const useLinkProductsModal = (props: LinkProductsModalProps): ViewProps => {
    const { currentAccount } = useAccountRepository();
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
    const [productObjects, setProductObjects] = useState<IProduct[]>([]);
    const [productAttributeValues, setProductAttributeValues] = useState<Record<string, string | null>>({});
    const existingProductIdsRef = useRef<string[]>([]);

    // Fetch existing product attributes for this attribute
    useEffect(() => {
        if (!props.attributeId) return;

        ProductAttributeClient.fetchProductAttributes({ attribute_id: props.attributeId })
            .then((response) => {
                const productIds = response.results.map((pa) => pa.product_id);
                existingProductIdsRef.current = productIds;
            })
            .catch((error) => {
                ErrorHandler.handleError({ error: error as Error });
            });
    }, [props.attributeId]);

    const fetchAvailableProducts = async (queryParams?: any) => {
        const response = await ProductClient.fetchProducts(queryParams);
        // Use ref to get the latest value
        const filteredResults = response.results.filter((product) => !existingProductIdsRef.current.includes(product.id));
        return {
            ...response,
            results: filteredResults,
            count: filteredResults.length
        };
    };

    useEffect(() => {
        if (selectedProducts.length === 0) {
            setProductObjects([]);
            setProductAttributeValues({});
            return;
        }

        ProductClient.resolveProducts(selectedProducts)
            .then((response) => {
                setProductObjects(response.results);
                setProductAttributeValues((prev) => {
                    const newValues = { ...prev };
                    response.results.forEach((product) => {
                        if (!(product.id in newValues)) {
                            newValues[product.id] = null;
                        }
                    });
                    Object.keys(newValues).forEach((productId) => {
                        if (!selectedProducts.includes(productId)) {
                            delete newValues[productId];
                        }
                    });
                    return newValues;
                });
            })
            .catch((error) => {
                ErrorHandler.handleError({ error: error as Error });
            });
    }, [selectedProducts]);

    const onAttributeValueChange = (productId: string, valueId: string | null) => {
        setProductAttributeValues((prev) => ({
            ...prev,
            [productId]: valueId
        }));
    };

    const isSubmitDisabled = () => {
        if (selectedProducts.length === 0) return true;
        return selectedProducts.some((productId) => !productAttributeValues[productId]);
    };

    const onSubmit = async () => {
        if (!currentAccount || selectedProducts.length === 0) {
            throw new Error(intl.formatMessage({ id: 'link-products-modal.error-no-products', defaultMessage: 'No products selected' }));
        }
        const missingValues = selectedProducts.filter((productId) => !productAttributeValues[productId]);
        if (missingValues.length > 0) {
            throw new Error(
                intl.formatMessage({
                    id: 'link-products-modal.error-missing-values',
                    defaultMessage: 'Please assign attribute values to all selected products'
                })
            );
        }

        try {
            await Promise.all(
                selectedProducts.map((productId) =>
                    ProductAttributeClient.postProductAttribute(currentAccount, productId, {
                        attribute_id: props.attributeId,
                        value_id: productAttributeValues[productId]
                    })
                )
            );
            if (props.successCallback) {
                props.successCallback();
            }
            dispatch(triggerRefetch({ entityType: EntityType.ATTRIBUTE, entityId: props.attributeId }));
            return { success: true };
        } catch (error) {
            ErrorHandler.handleError({ error: error as Error });
            throw error;
        }
    };

    return {
        currentAccount: currentAccount || '',
        formValues: { product_ids: selectedProducts },
        selectedProducts,
        productObjects,
        productAttributeValues,
        fetchAvailableProducts,
        setSelectedProducts,
        onAttributeValueChange,
        onSubmit,
        isSubmitDisabled
    };
};
